import React, { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { PHILOSOPHY_GROUPS } from "../data/philosophy";

export default function PhilosophyPage({ open, section, onClose }) {
  useEffect(() => {
    if (!open || !section) return;
    const t = setTimeout(() => document.getElementById(`philosophy-${section}`)?.scrollIntoView({ block: "start" }), 60);
    return () => clearTimeout(t);
  }, [open, section]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const jump = (id) => document.getElementById(`philosophy-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="philosophy-page"
          className="fixed inset-0 z-50 overflow-y-auto bg-[#04040b]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
        >
          <button
            data-testid="philosophy-close"
            onClick={onClose}
            aria-label="Close"
            className="fixed right-5 top-5 z-10 rounded-full border border-white/10 bg-white/5 p-2 text-slate-300 transition-colors hover:bg-white/15 hover:text-white"
          >
            <X size={20} />
          </button>

          <div className="mx-auto max-w-2xl px-6 pb-24 pt-20 sm:pt-24">
            <img src="/breathtones-icon.png" alt="" className="mb-6 h-16 w-16" />
            <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-cyan-300/70">BreathTones</p>
            <h2 className="mt-2 font-serif text-4xl text-white sm:text-5xl">Guide & Philosophy</h2>

            <nav className="mt-8 space-y-4" data-testid="philosophy-contents">
              {PHILOSOPHY_GROUPS.map((g) => (
                <div key={g.id}>
                  <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">{g.title}</p>
                  <div className="flex flex-wrap gap-2">
                    {g.sections.map((s) => (
                      <button
                        key={s.id}
                        data-testid={`philosophy-link-${s.id}`}
                        onClick={() => jump(s.id)}
                        className="rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-xs text-slate-300 transition-colors hover:bg-white/15 hover:text-white"
                      >
                        {s.title}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </nav>

            {PHILOSOPHY_GROUPS.map((g) => (
              <div key={g.id} data-testid={`philosophy-group-${g.id}`}>
                <p className="mt-20 font-mono text-[11px] uppercase tracking-[0.35em] text-cyan-300/70">{g.title}</p>
                {g.sections.map((s) => (
                  <section key={s.id} id={`philosophy-${s.id}`} data-testid={`philosophy-section-${s.id}`} className="mt-10 scroll-mt-20">
                    <h3 className="font-serif text-2xl text-amber-200 sm:text-3xl">{s.title}</h3>
                    <div className="mt-2 h-px w-16 bg-gradient-to-r from-amber-300/60 to-transparent" />
                    {s.paragraphs.length ? (
                      s.paragraphs.map((p, i) => (
                        <p key={i} className="mt-5 font-serif text-lg leading-relaxed text-slate-200">{p}</p>
                      ))
                    ) : (
                      <p className="mt-5 font-serif text-lg italic text-slate-500">Your words will appear here.</p>
                    )}
                  </section>
                ))}
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
